import { Dict } from 'mixpanel-browser';
import { isAuthenticatedMailbox } from '../../model/authenticatedMailbox';
import Mailbox from '../../model/mailbox';
import { getErrorMessage } from '../../utilities/errorHelper';
import { EventName, ErrorDict } from './providers/analyticsProvider';
import FacebookAnalyticsProvider from './providers/facebookAnalyticsProvider';
import GoogleAnalyticsProvider from './providers/googleAnalyticsProvider';
import MixpanelAnalyticsProvider from './providers/mixpanelAnalyticsProvider';
import TrackJsAnalyticsProvider from './providers/trackJsAnalyticsProvider';
import TikTokAnalyticsProvider from './providers/tikTokAnalyticsProvider';
import TwitterAnalyticsProvider from './providers/twitterAnalyticsProvider';
import GtmAnalyticsProvider from './providers/gtmAnalyticsProvider';
import BingAnalyticsProvider from './providers/bingAnalyticsProvider';
import { IdentifyParams, CheckoutParams } from './analyticsService.types';
import LinkedInAnalyticsProvider from './providers/linkedInAnalyticsProvider';
import SingularAnalyticsProvider from './providers/singularAnalyticsProvider';

const mixpanelProvider = new MixpanelAnalyticsProvider();
const facebookProvider = new FacebookAnalyticsProvider();
const googleProvider = new GoogleAnalyticsProvider();
const trackJsProvider = new TrackJsAnalyticsProvider();
const tikTokProvider = new TikTokAnalyticsProvider();
const twitterProvider = new TwitterAnalyticsProvider();
const gtmProvider = new GtmAnalyticsProvider();
const bingProvider = new BingAnalyticsProvider();
const linkedInProvider = new LinkedInAnalyticsProvider();
const singularProvider = new SingularAnalyticsProvider();

export default class AnalyticsService {
  static identify(params: IdentifyParams) {
    mixpanelProvider.identify(params);
    facebookProvider.identify(params);
    googleProvider.identify(params);
    gtmProvider.identify(params);
    trackJsProvider.identify(params);
    tikTokProvider.identify(params);
    twitterProvider.identify(params);
    linkedInProvider.identify(params);
    singularProvider.identify(params);
  }

  static alias(mailbox: Mailbox) {
    const id = mailbox.email_address;
    mixpanelProvider.alias(id);
    facebookProvider.alias(id);
    googleProvider.alias(id);
    tikTokProvider.alias(id);
    twitterProvider.alias(id);
    linkedInProvider.alias(id);
  }

  static unidentify() {
    singularProvider.unidentify();
  }

  static trackListEvent(name: EventName, listSender: string, props?: Dict) {
    const unsubscribeProps = {
      ...props,
      listSender,
    };
    mixpanelProvider.track(name, unsubscribeProps);
    facebookProvider.track(name, unsubscribeProps);
    googleProvider.track(name, unsubscribeProps);
    tikTokProvider.track(name, unsubscribeProps);
    twitterProvider.track(name, unsubscribeProps);
    linkedInProvider.track(name, unsubscribeProps);
    singularProvider.track(name, unsubscribeProps);
  }

  static track(name: EventName, props?: Dict) {
    mixpanelProvider.track(name, props);
    facebookProvider.track(name, props);
    googleProvider.track(name, props);
    gtmProvider.track(name, props);
    tikTokProvider.track(name, props);
    twitterProvider.track(name, props);
    bingProvider.track(name, props);
    linkedInProvider.track(name, props);
    singularProvider.track(name, props);
  }

  static trackCheckout(checkoutParams: CheckoutParams) {
    mixpanelProvider.trackCheckout(checkoutParams);
    facebookProvider.trackCheckout(checkoutParams);
    googleProvider.trackCheckout(checkoutParams);
    gtmProvider.trackCheckout(checkoutParams);
    tikTokProvider.trackCheckout(checkoutParams);
    twitterProvider.trackCheckout(checkoutParams);
    bingProvider.trackCheckout(checkoutParams);
    linkedInProvider.trackCheckout(checkoutParams);
    singularProvider.trackCheckout(checkoutParams);
  }

  static trackListError(name: EventName, listSender: string, e: unknown, props?: Dict) {
    const errorProps: Dict = {
      ...(props ? props : {}),
      listSender,
    };
    this.trackError(name, e, errorProps);
  }

  static trackError(name: EventName, e: unknown, props?: Dict) {
    console.error(e);

    const errorMessage: string = getErrorMessage(e);
    const errorStack: string | undefined = (e as Error).stack;
    const errorProps: ErrorDict = {
      ...(props ? props : {}),
      errorMessage,
      ...(errorStack ? { stack: errorStack } : {}),
      isError: true,
    };

    mixpanelProvider.trackError(name, errorProps);
    facebookProvider.trackError(name, errorProps);
    googleProvider.trackError(name, errorProps);
  }

  static async logCheckoutConversion(checkoutSession: any) {
    // probably shouldn't be dividing this
    let purchaseValue: number = checkoutSession.amount_total / 100;
    const currency: string | undefined = checkoutSession.currency?.toUpperCase();
    const transactionId: string = checkoutSession.id;

    if (purchaseValue === 0) {
      purchaseValue = 12.00; // Handling the free trial case.  Instead of giving the value of the 39.99 annual plan we're giving the value 12.00 since it accounts for 30% conversion to paid
    }

    this.trackCheckout({ value: purchaseValue, currency, transactionId });
  }

  static async setTestGroup(testName: string, testGroup: string, isNewTestRegistration = false) {
    await mixpanelProvider.setTestGroup(testName, testGroup, isNewTestRegistration);
  }

  static setFacebookUser() {
    mixpanelProvider.setFacebookUser();
  }

  static setPeople(props: Dict) {
    mixpanelProvider.setPeople(props);
  }
}
